import { Link } from "react-router-dom";
import { Wind, Gauge, ShieldCheck, Radio, Cpu, Layers } from "lucide-react";
import RevealLine from "@/components/RevealLine";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n";

const SERVICES = [
  {
    icon: Wind,
    n: "01",
    title: { it: "Purificazione professionale", en: "Professional purification" },
    desc: {
      it: "Progettazione e installazione di sistemi di purificazione ad alta portata: HEPA H13/H14, carboni attivi, sterilizzazione UV-C. Per abitazioni, uffici, cliniche, scuole e produzione.",
      en: "Design and installation of high-flow purification systems: HEPA H13/H14, activated carbon, UV-C sterilisation. For homes, offices, clinics, schools and production sites.",
    },
    tags: ["HEPA H13/H14", "UV-C", { it: "Carboni attivi", en: "Activated carbon" }, "Cleanroom"],
  },
  {
    icon: Gauge,
    n: "02",
    title: { it: "Monitoraggio smart qualità aria", en: "Smart air quality monitoring" },
    desc: {
      it: "Sensori intelligenti integrati in ogni purificatore (PM2.5, VOC, temperatura, umidità) e App Aéràs per iOS e Android: qualità dell'aria in tempo reale, modalità automatiche e gestione remota di ogni funzione.",
      en: "Smart sensors built into every purifier (PM2.5, VOC, temperature, humidity) and the Aéràs App for iOS and Android: real-time air quality, automatic modes and remote control of every function.",
    },
    tags: ["PM2.5/PM10", "CO₂/VOC", "App iOS/Android", "Wi-Fi"],
  },
  {
    icon: Layers,
    n: "03",
    title: { it: "HVAC & trattamento aria", en: "HVAC & air treatment" },
    desc: {
      it: "Unità di trattamento aria (UTA) centralizzate per grandi volumi. Recupero termico >85%, ventilatori EC, sanificazione UV-C in-duct, sanificazione filtri programmata.",
      en: "Centralised air handling units (AHU) for large volumes. >85% heat recovery, EC fans, in-duct UV-C sanitisation, scheduled filter sanitisation.",
    },
    tags: ["UTA", { it: "Recupero termico", en: "Heat recovery" }, "EC fans", { it: "Sanificazione", en: "Sanitisation" }],
  },
  {
    icon: Radio,
    n: "04",
    title: { it: "Analisi ambientale & audit", en: "Environmental analysis & audit" },
    desc: {
      it: "Sopralluoghi, misure di riferimento, mappe di concentrazione, calcolo carichi. Restituiamo un dossier tecnico con raccomandazioni e simulazione ROI.",
      en: "Site surveys, baseline measurements, concentration maps, load calculations. We deliver a technical dossier with recommendations and ROI simulation.",
    },
    tags: ["Audit", { it: "Mappe", en: "Maps" }, "ROI", "Dossier"],
  },
  {
    icon: Cpu,
    n: "05",
    title: { it: "Manutenzione predittiva", en: "Predictive maintenance" },
    desc: {
      it: "Contratti di service basati su dati: sensori, telemetria e algoritmi predittivi determinano gli interventi. Riduzione fermi macchina e costi di gestione.",
      en: "Data-driven service contracts: sensors, telemetry and predictive algorithms determine interventions. Reduced downtime and operating costs.",
    },
    tags: ["Service", "SLA 24h", { it: "Predittiva", en: "Predictive" }, "Data-driven"],
  },
  {
    icon: ShieldCheck,
    n: "06",
    title: { it: "Carbon accounting (Carbonplai)", en: "Carbon accounting (Carbonplai)" },
    desc: {
      it: "In partnership con Carbonplai: misuriamo CO₂ equivalente di ogni impianto, produciamo report trimestrali e proponiamo compensazione Gold Standard.",
      en: "In partnership with Carbonplai: we measure each system's CO₂ equivalent, produce quarterly reports and propose Gold Standard offsetting.",
    },
    tags: ["Carbonplai", "CO₂", "Gold Standard", "Report ESG"],
  },
];

export default function Services() {
  const { lang, tr } = useLang();
  return (
    <div data-testid="page-services" className="bg-white text-[#0a0a0a] pt-32 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-8">
          {tr({ it: "— SERVIZI / SOLUZIONI", en: "— SERVICES / SOLUTIONS" })}
        </div>
        <h1 className="font-display text-[clamp(3rem,10vw,10rem)] leading-[0.86] tracking-tighter">
          <div><RevealLine delay={0.2}>{tr({ it: "Sei servizi.", en: "Six services." })}</RevealLine></div>
          <div>
            <RevealLine delay={0.35} className="font-editorial italic font-normal text-[#3f3f46]">
              {tr({ it: "Un solo standard.", en: "One standard." })}
            </RevealLine>
          </div>
        </h1>
        <Reveal delay={0.5} className="mt-16 max-w-2xl">
          <p className="font-body text-lg text-[#3f3f46] leading-relaxed">
            {tr({
              it: "Copriamo l'intero ciclo di vita di un impianto d'aria: dall'audit iniziale all'installazione, dalla telemetria al carbon reporting. Ogni servizio è modulare e integrabile con i sistemi esistenti.",
              en: "We cover the full lifecycle of an air system: from the initial audit to installation, from telemetry to carbon reporting. Every service is modular and integrates with existing systems.",
            })}
          </p>
        </Reveal>
      </section>

      <section className="mt-24 md:mt-32 border-t border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-b border-l border-[#e4e4e7] mt-px">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.n} delay={i * 0.05}>
                <div
                  data-testid={`service-${s.n}`}
                  className="border-r border-t border-[#e4e4e7] p-8 md:p-10 h-full min-h-[420px] flex flex-col justify-between bg-white hover:bg-[#f4f4f5] transition-colors duration-500 group"
                >
                  <div>
                    <div className="flex items-start justify-between mb-8">
                      <Icon size={26} className="text-[#007A8A]" />
                      <div className="font-mono text-[10px] tracking-[0.3em] text-[#52525b]">{s.n}</div>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tighter leading-[1.05] mb-4">
                      {tr(s.title)}
                    </h3>
                    <p className="font-body text-sm text-[#3f3f46] leading-relaxed">{tr(s.desc)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-8">
                    {s.tags.map((t, j) => (
                      <span
                        key={j}
                        className="font-mono text-[10px] tracking-widest uppercase px-2 py-1 border border-[#e4e4e7] text-[#3f3f46]"
                      >
                        {tr(t)}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="mt-24 border-t border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <h4 className="font-display text-4xl md:text-6xl tracking-tighter max-w-2xl">
            {tr({ it: "Non trovi il servizio che ti serve?", en: "Can't find the service you need?" })}{" "}
            <span className="font-editorial italic font-normal text-[#3f3f46]">
              {tr({ it: "Progettiamolo insieme.", en: "Let's design it together." })}
            </span>
          </h4>
          <Link to="/contatti" className="btn-primary" data-testid="services-cta">
            {tr({ it: "Parliamone", en: "Let's talk" })} →
          </Link>
        </div>
      </section>
    </div>
  );
}
